import React, { Component } from "react";
import PropTypes from "prop-types";
import { defineMessage, FormattedMessage, injectIntl } from "react-intl";
import classNames from "classnames";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons/faCloudUploadAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import configs from "../utils/configs";
import IfFeature from "./if-feature";
import { fetchReticulumAuthenticated } from "../utils/phoenix-utils";
import { upload } from "../utils/media-utils";
import { ensureAvatarMaterial, fetchAvatar } from "../utils/avatar-utils";

import AvatarPreview from "./avatar-preview";
import styles from "../assets/stylesheets/avatar-editor.scss";
import AvatarCreator from "../onland/react-components/avatar-creator";

import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

import dropdownArrowUrl from "../assets/images/dropdown_arrow.png";
import dropdownArrow2xUrl from "../assets/images/dropdown_arrow@2x.png";

import { ReactComponent as ArrowDown } from "./icons/keydown-2.svg";
import { ReactComponent as ArrowUp } from "./icons/keyup-2.svg";
import { ReactComponent as Close } from "./icons/Close-2.svg";
import { ReactComponent as Check } from "./icons/CheckGreen.svg";
import { BackButtonLabel } from "./input/BackButtonLabel";
import { Modal } from "./modal/Modal";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { RenameAvatarDialog } from "./room/RenameAvatarDialog";
import { DeleteAvatarDialog } from "./room/DeleteAvatarDialog";

const delistAvatarInfoMessage = defineMessage({
  id: "avatar-editor.delist-avatar-info",
  defaultMessage:
    "Other users already using this avatar will still be able to use it, but it will be removed from 'My Avatars' and search results."
});

const AVATARS_API = "/api/v1/avatars";

const defaultEditors = [
  // TODO This previously contain tryquilt.io.  We should re-evaluate whether these types of editors are still desired,
  // and change the related code accordingly.
];
const useAllowedEditors = true;
const allowedEditors = [
  ...defaultEditors,
  {
    name: "Skindex Editor",
    url: "https://www.minecraftskins.com/skin-editor"
  },
  {
    name: "MinecraftSkins.net Editor",
    url: "https://www.minecraftskins.net/skineditor"
  }
];


// GLTFLoader plugin for splitting glTF and bin from glb.
class GLTFBinarySplitterPlugin {
  constructor(parser) {
    this.parser = parser;
    this.gltf = null;
    this.bin = null;
  }

  beforeRoot() {
    const parser = this.parser;
    const { body } = parser.extensions.KHR_binary_glTF;
    const content = JSON.stringify(ensureAvatarMaterial(parser.json));

    this.gltf = new File([content], "file.gltf", {
      type: "model/gltf"
    });
    this.bin = new File([body], "file.bin", {
      type: "application/octet-stream"
    });

    // This plugin just wants to split gltf and bin from glb and
    // doesn't want to start the parse. But glTF loader plugin API
    // doesn't have an ability to cancel the parse. So overriding
    // parser.json with very light glTF data as workaround.
    parser.json = { asset: { version: "2.0" } };
  }

  afterRoot(result) {
    result.files = result.files || {};
    result.files.gltf = this.gltf;
    result.files.bin = this.bin;
  }
}

class AvatarEditor extends Component {
  static propTypes = {
    avatarId: PropTypes.string,
    onSave: PropTypes.func,
    onClose: PropTypes.func,
    hideDelete: PropTypes.bool,
    debug: PropTypes.bool,
    className: PropTypes.string,
    intl: PropTypes.object.isRequired,
    isCreating: PropTypes.bool,
    creationModal: PropTypes.string
  };

  state = {
    baseAvatarResults: [],
    editorLinks: defaultEditors,
    previewGltfUrl: null,
    showAdvancedOptions: false,
    showModalSave: false,
    progressUploading: 0,
    errorUploading: false
  };

  constructor(props) {
    super(props);
    this.inputFiles = {};
    this.timeoutId = null;
  }

  componentWillUnmount() {
    // Clear timeout when component unmounts
    clearTimeout(this.timeoutId);
  }

  componentDidMount = async () => {
    // Try to remove fullscren layout when avatar-editor is used to improve performance
    window.APP.mediaSearchStore.pushExitMediaBrowserHistory(window.APP.history, false);

    let avatar = {};
    try {
      if (this.props.avatarId) avatar = await fetchAvatar(this.props.avatarId);
    } catch (err) {
      avatar = {};
    }

    if (this.props.isCreating && avatar.avatar_id) {
      let baseAvatarResults = {};
      baseAvatarResults = [avatar].map(e => ({
        id: e.avatar_id,
        name: e.name,
        gltfs: {
          avatar: e.gltf_url,
          base: e.base_gltf_url
        },
        images: {
          preview: {
            height: 1280,
            url: e.files.thumbnail,
            width: 720
          }
        }
      }));
      avatar.creatorAttribution = (avatar.attributions && avatar.attributions.creator) || "";
      Object.assign(this.inputFiles, avatar.files);
      this.setState({ avatar, previewGltfUrl: avatar.base_gltf_url, baseAvatarResults });
    } else if (avatar.avatar_id) {
      avatar.creatorAttribution = (avatar.attributions && avatar.attributions.creator) || "";
      Object.assign(this.inputFiles, avatar.files);
      this.setState({ avatar, previewGltfUrl: avatar.base_gltf_url });
    } else {
      const { entries } = await fetchReticulumAuthenticated(`/api/v1/media/search?filter=base&source=avatar_listings`);
      const baseAvatarResults = entries.map(e => ({ id: e.id, name: e.name, gltfs: e.gltfs, images: e.images }));
      if (baseAvatarResults.length) {
        const randomAvatarResult = baseAvatarResults[Math.floor(Math.random() * baseAvatarResults.length)];
        this.setState({
          baseAvatarResults,
          avatar: {
            name: "My Avatar",
            files: {},
            base_gltf_url: randomAvatarResult.gltfs.base,
            parent_avatar_listing_id: randomAvatarResult.id
          },
          previewGltfUrl: randomAvatarResult.gltfs.avatar
        });
      } else {
        this.setState({
          avatar: {
            name: "My Avatar",
            files: {}
          }
        });
      }
    }
  };

  componentDidUpdate(prevProps) {
    // If the creation modal is upload, make a click on the hidden file input to trigger the upload
    if (this.props.creationModal === "upload" && prevProps.creationModal !== "upload") {
      document.querySelector("#avatar-file_glb").click();
    }
  }

  handleInputChange = (e, name) => {
    this.setState({ avatar: { ...this.state.avatar, [name]: e.target.value } });
    return;
  };

  calculateUploadProgress = (loaded, totalBytes, currProgress = 0, limit = 66.0) => {
    // Ensure the input is valid to avoid division by zero or negative values.
    if (totalBytes > 0 && loaded >= 0 && loaded <= totalBytes) {
      const progress = Math.min((loaded / totalBytes) * 100 + parseFloat(currProgress), limit);
      return progress.toFixed(2); // Returns the progress as a string with two decimal places.
    } else {
      // Handle invalid input values by returning an error message or throwing an exception.
      return "Invalid input for progress calculation.";
    }
  };

  createOrUpdateAvatar = avatar => {
    return fetchReticulumAuthenticated(
      avatar.avatar_id && !this.props.isCreating ? `${AVATARS_API}/${avatar.avatar_id}` : AVATARS_API,
      avatar.avatar_id && !this.props.isCreating ? "PUT" : "POST",
      { avatar }
    ).then(({ avatars }) => avatars[0]);
  };

  uploadAvatar = async e => {
    e.preventDefault();
    this.closeSaveModal();

    try {
      this.setState({ uploading: true });
      if (this.inputFiles.glb) {
        const gltfLoader = new GLTFLoader().register(parser => new GLTFBinarySplitterPlugin(parser));
        const gltfUrl =
          this.inputFiles.glb instanceof File ? URL.createObjectURL(this.inputFiles.glb) : this.inputFiles.glb;

        const onProgress = data => {
          const { loaded, total } = data;
          const progress = this.calculateUploadProgress(loaded, total, this.state.progressUploading, 33.33);
          if (progress) {
            this.setState({ progressUploading: progress });
          }
        };
        await new Promise((resolve, reject) => {
          // GLTFBinarySplitterPlugin saves gltf and bin in gltf.files
          gltfLoader.load(
            gltfUrl,
            result => {
              this.inputFiles.gltf = result.files.gltf;
              this.inputFiles.bin = result.files.bin;
              resolve(result);
            },
            onProgress,
            reject
          );
        });
        URL.revokeObjectURL(gltfUrl);
      }

      if (this.inputFiles.glb && "string" === typeof this.inputFiles.glb) {
        await this.getAvatarFiles(this.inputFiles.glb);
      }

      // loadInterval = setInterval(() => {
      //   const newProgress = Math.min((parseInt(this.state.progressUploading) + 1),99)
      //   this.setState({progressUploading: newProgress})
      // }, 700)
    } catch (e) {
      console.error("error", e);
      this.setState({ uploading: false });
      this.setState({ progressUploading: null });
      this.setState({ errorUploading: true });
    }

    this.inputFiles.thumbnail = new File([await this.preview.snapshot()], "thumbnail.png", {
      type: "image/png"
    });

    const filesToUpload = ["gltf", "bin", "base_map", "emissive_map", "normal_map", "orm_map", "thumbnail"].filter(
      k => this.inputFiles[k] === null || this.inputFiles[k] instanceof File
    );

    const fileUploads = await Promise.all(filesToUpload.map(f => this.inputFiles[f] && upload(this.inputFiles[f])));
    const avatar = {
      ...this.state.avatar,
      attributions: {
        creator: this.state.avatar.creatorAttribution
      },
      files: fileUploads
        .map((resp, i) => [filesToUpload[i], resp && [resp.file_id, resp.meta.access_token, resp.meta.promotion_token]])
        .reduce((o, [k, v]) => ({ ...o, [k]: v }), {})
    };
    try {
      const newAvatar = await this.createOrUpdateAvatar(avatar);
      if (this.props.onSave) this.props.onSave(newAvatar);
      this.setState({ uploading: false });
    } catch (e) {
      console.error("error", e);
      this.setState({ uploading: false });
      this.setState({ progressUploading: null });
      this.setState({ errorUploading: true });
    }
  };

  deleteAvatar = async e => {
    e.preventDefault();
    await fetchReticulumAuthenticated(`${AVATARS_API}/${this.state.avatar.avatar_id}`, "DELETE");
    if (this.props.onSave) this.props.onSave();
  };

  toggleAdvancedOptions = async () => {
    this.setState({ showAdvancedOptions: !this.state.showAdvancedOptions });
  };

  toggleSaveModal = () => {
    this.setState({ showModalSave: !this.state.showModalSave });
  };

  closeSaveModal = () => {
    this.setState({ showModalSave: false });
  };

  fileField = (name, label, accept, disabled = false, title) => (
    <div className={classNames("file-input-row", { disabled })} key={name} title={title}>
      <label htmlFor={`avatar-file_${name}`}>
        <div className="img-box" />
        <span>{label}</span>
      </label>
      <input
        id={`avatar-file_${name}`}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={e => {
          const file = e.target.files[0];
          e.target.value = null;
          this.inputFiles[name] = file;
          URL.revokeObjectURL(this.state.previewGltfUrl);
          const previewGltfUrl = URL.createObjectURL(this.inputFiles.glb);
          this.setState({
            previewGltfUrl,
            avatar: {
              ...this.state.avatar,
              files: {
                ...this.state.avatar.files,
                [name]: URL.createObjectURL(file)
              }
            }
          });
        }}
      />
      {this.state.avatar.files[name] && (
        <a
          onClick={() => {
            this.inputFiles[name] = null;
            URL.revokeObjectURL(this.state.avatar.files[name]);
            this.setState(
              {
                avatar: {
                  ...this.state.avatar,
                  files: {
                    ...this.state.avatar.files,
                    [name]: null
                  }
                }
              },
              () => this.setState({ previewGltfUrl: this.getPreviewUrl() })
            );
          }}
        >
          <i>
            <FontAwesomeIcon icon={faTimes} />
          </i>
        </a>
      )}
    </div>
  );

  mapField = (name, label, accept, disabled = false, title) => (
    <div className="file-input-row" key={name} title={title}>
      <label htmlFor={`avatar-file_${name}`}>
        <div className="img-box">
          {this.state.avatar.files[name] ? (
            <img src={this.state.avatar.files[name]} />
          ) : (
            <FontAwesomeIcon icon={faCloudUploadAlt} />
          )}
        </div>
        <span>{label}</span>
      </label>
      <input
        id={`avatar-file_${name}`}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={e => {
          const file = e.target.files[0];
          e.target.value = null;
          this.inputFiles[name] = file;
          URL.revokeObjectURL(this.state.avatar.files[name]);
          this.setState({
            avatar: {
              ...this.state.avatar,
              files: {
                ...this.state.avatar.files,
                [name]: URL.createObjectURL(file)
              }
            }
          });
        }}
      />
      {this.state.avatar.files[name] && (
        <a
          onClick={() => {
            this.inputFiles[name] = null;
            URL.revokeObjectURL(this.state.avatar.files[name]);
            this.setState({
              avatar: {
                ...this.state.avatar,
                files: {
                  ...this.state.avatar.files,
                  [name]: null
                }
              }
            });
          }}
        >
          <i>
            <FontAwesomeIcon icon={faTimes} />
          </i>
        </a>
      )}
    </div>
  );

  textField = (name, placeholder, disabled, required, hideLabel) => (
    <div className="text-field-container">
      {!hideLabel && <label htmlFor={`#avatar-${name}`}>{placeholder}</label>}
      <input
        id={`avatar-${name}`}
        type="text"
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        className="text-field"
        value={this.state.avatar[name] || ""}
        onChange={e => this.handleInputChange(e, name)}
      />
    </div>
  );

  // Return the gltf for the selected base avatar, the locally modified glb, or the avatar's base_gltf_url
  getPreviewUrl = baseSid => {
    if (baseSid) {
      const avatarResult = this.state.baseAvatarResults.find(a => a.id === baseSid);
      if (avatarResult) return avatarResult.gltfs.avatar;
    }

    return this.inputFiles.glb ? URL.createObjectURL(this.inputFiles.glb) : this.state.avatar.base_gltf_url;
  };

  selectListingField = (propName, placeholder) => (
    <div className="select-container">
      <label htmlFor={`#avatar-${propName}`}>{placeholder}</label>
      <select
        id={`avatar-${propName}`}
        value={this.state.avatar[propName] || ""}
        onChange={async e => {
          const sid = e.target.value;
          this.setState({ avatar: { ...this.state.avatar, [propName]: sid }, previewGltfUrl: this.getPreviewUrl(sid) });
        }}
        placeholder={placeholder}
        className="select"
      >
        {this.state.baseAvatarResults.map(a => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
        <option value="">
          <FormattedMessage id="avatar-editor.custom-avatar-option" defaultMessage="Custom GLB..." />
        </option>
      </select>
      <img className="arrow" src={dropdownArrowUrl} srcSet={`${dropdownArrow2xUrl} 2x`} />
    </div>
  );

  selectListingGrid = (propName, placeholder) => (
    <div className="select-grid-container">
      <div className={styles.avatarTypeSelectorContainer}>
        <div className={styles.avatarTypeSelector}>
          <div className={styles.avatarSelectorImage}></div>
          <h2>
            <FormattedMessage id="audio-debug-panel.audio-debug-avatar-title" defaultMessage="Avatar" />
          </h2>
          <p>
            <FormattedMessage
              id="avatar-editor.create-avatar-option"
              defaultMessage="Create your half or full body avatar"
            />
          </p>
          <Button
            preset="accent4"
            onClick={() => this.showCreationModal("cartoons")}
            className="custom-avatar-button"
            type="button"
          >
            <span>
              <FormattedMessage id="avatar-editor.create" defaultMessage="Create" />
            </span>
          </Button>
        </div>
        <div className={styles.avatarTypeSelector}>
          <div className={classNames(styles.avatarSelectorImage, "custom-avatar")}></div>
          <h2>
            <FormattedMessage id="avatar-editor.create-custom-avatar" defaultMessage="Custom Avatar" />
          </h2>
          <p>
            <FormattedMessage
              id="avatar-editor.create-custom-avatar-text"
              defaultMessage={"Upload a GLB file, you can be whatever you want"}
            />
          </p>
          <div style={{ marginTop: "auto" }}>
            <input
              id="avatar-file_glb"
              type="file"
              accept="model/gltf+binary,.glb"
              onChange={e => {
                const file = e.target.files[0];
                e.target.value = null;
                this.inputFiles["glb"] = file;
                URL.revokeObjectURL(this.state.avatar.files["glb"]);
                this.setState({
                  avatar: {
                    ...this.state.avatar,
                    [propName]: "",
                    files: {
                      ...this.state.avatar.files,
                      glb: URL.createObjectURL(file)
                    }
                  },
                  previewGltfUrl: this.getPreviewUrl("")
                });
                this.toggleSaveModal();
              }}
            />

            <Button
              as="label"
              htmlFor="avatar-file_glb"
              preset="accent7"
              className="custom-avatar-button upload-avatar"
              type="button"
            >
              <FormattedMessage id="place-popover.item-type.upload" defaultMessage={"Upload"} />
            </Button>
          </div>
        </div>
      </div>
      {/* <label htmlFor={`#avatar-${propName}`}>{placeholder}</label> */}
      <div className="select-grid">
        {this.state.baseAvatarResults.map(a => (
          <div
            onClick={() =>
              this.setState({
                avatar: { ...this.state.avatar, [propName]: a.id },
                previewGltfUrl: this.getPreviewUrl(a.id)
              })
            }
            key={a.id}
            className={classNames("item", { selected: a.id === this.state.avatar[propName] })}
            style={{ paddingBottom: `${(a.images.preview.width / a.images.preview.height) * 100}%` }}
          >
            <img src={a.images.preview.url} />
          </div>
        ))}
      </div>
      <input
        id="avatar-file_glb"
        type="file"
        accept="model/gltf+binary,.glb"
        onChange={e => {
          const file = e.target.files[0];
          e.target.value = null;
          this.inputFiles["glb"] = file;
          URL.revokeObjectURL(this.state.avatar.files["glb"]);
          this.setState({
            avatar: {
              ...this.state.avatar,
              [propName]: "",
              files: {
                ...this.state.avatar.files,
                glb: URL.createObjectURL(file)
              }
            },
            previewGltfUrl: this.getPreviewUrl("")
          });
        }}
      />
    </div>
  );

  textarea = (name, placeholder, disabled) => (
    <div>
      <textarea
        id={`avatar-${name}`}
        disabled={disabled}
        placeholder={placeholder}
        className="textarea"
        value={this.state.avatar[name] || ""}
        onChange={e => this.setState({ avatar: { ...this.state.avatar, [name]: e.target.value } })}
      />
    </div>
  );

  checkbox = (name, title, children, disabled) => (
    <div className="checkbox-container" title={title}>
      <input
        id={`avatar-${name}`}
        type="checkbox"
        className={classNames("checkbox", !!this.state.avatar[name] ? "checked" : "")}
        disabled={disabled}
        checked={!!this.state.avatar[name]}
        onChange={e => this.setState({ avatar: { ...this.state.avatar, [name]: e.target.checked } })}
      />
      {!!this.state.avatar[name] && (
        <label
          htmlFor={`#avatar-${name}`}
          className="check-mark"
          onClick={() => this.setState({ avatar: { ...this.state.avatar, [name]: !this.state.avatar[name] } })}
        >
          <Check></Check>
        </label>
      )}
      <span htmlFor={`#avatar-${name}`}>{children}</span>
    </div>
  );

  handleGltfLoaded = gltf => {
    const ext = gltf.parser.json.extensions && gltf.parser.json.extensions["MOZ_hubs_avatar"];
    let editorLinks = (ext && ext.editors) || defaultEditors;
    if (useAllowedEditors) {
      editorLinks = editorLinks.filter(e => allowedEditors.some(w => w.name === e.name && w.url === e.url));
    }
    this.setState({ editorLinks });
  };

  showCreationModal = type => {
    this.setState({
      creationModal: type
    });
  };

  closeCreationModal = () => {
    this.setState({
      creationModal: ""
    });
  };

  handleAvatarCreated = async gltfUrl => {
    this.setState({
      avatar: {
        ...this.state.avatar,
        parent_avatar_listing_id: "",
        files: {
          ...this.state.avatar.files,
          glb: gltfUrl
        }
      },
      previewGltfUrl: gltfUrl,
      uploading: true
    });

    this.inputFiles.glb = gltfUrl;

    this.toggleSaveModal();
  };

  getAvatarFiles = gltfUrl => {
    const gltfLoader = new GLTFLoader().register(parser => new GLTFBinarySplitterPlugin(parser));

    const onProgress = event => {
      const { loaded, total } = event;
      const progress = this.calculateUploadProgress(loaded, total, this.state.progressUploading, 80.0);
      if (progress) {
        this.setState({ progressUploading: progress });
      }
    };

    return new Promise((resolve, reject) => {
      // GLTFBinarySplitterPlugin saves gltf and bin in gltf.files
      gltfLoader.load(
        gltfUrl,
        result => {
          this.inputFiles.gltf = result.files.gltf;
          this.inputFiles.bin = result.files.bin;
          resolve(result);
        },
        onProgress,
        reject
      );
    });
  };

  renderModal = () => {
    const { showModalSave } = this.state;
    return (
      <div className={classNames(styles.saveModal, !showModalSave ? "hide" : "")}>
        <div className={styles.saveModalBackground}></div>
        <Modal className={styles.saveModalCard}>
          <div className={styles.saveContainer}>
            <h2>Save Avatar</h2>
            <p>We're almost ready! To complete the creation you must save your avatar.</p>
            <div className="button-container">
              <Button preset="accent4" className="custom-avatar-button" type="button" onClick={this.uploadAvatar}>
                Save
              </Button>
              <Button preset="accent8" onClick={this.closeSaveModal} className="custom-avatar-button" type="button">
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  };

  render() {
    const { debug, intl } = this.props;
    const { avatar } = this.state;

    return (
      <div>
        {(this.props.creationModal === "cartoons" || this.props.creationModal === "upload") && (
          <div style={{ display: "none" }}>
            <AvatarPreview
              className="preview"
              avatarCreator={true}
              avatarGltfUrl={this.state.previewGltfUrl}
              onGltfLoaded={this.handleGltfLoaded}
              progress={this.state.progressUploading}
              hasError={this.state.errorUploading}
              {...this.inputFiles}
              ref={p => (this.preview = p)}
            />
          </div>
        )}

        {this.props.creationModal === "cartoons" && !this.state.uploading && (
          <AvatarCreator
            className={this.props.className}
            onClose={this.props.onClose}
            onCreate={this.handleAvatarCreated}
            modal="cartoons"
          />
        )}

        {this.props.creationModal === "rename" && (
          <RenameAvatarDialog
            avatarId={this.props.avatarId}
            open={this.props.creationModal === "rename"}
            onOpenChange={(_, data) => this.props.onClose(data.open)}
          />
        )}

        {this.props.creationModal === "delete" && (
          <DeleteAvatarDialog
            avatarId={this.props.avatarId}
            open={this.props.creationModal === "delete"}
            onOpenChange={(_, data) => this.props.onClose(data.open)}
          />
        )}

        {/*  Save avatar dialog */}
        <Dialog open={this.state.showModalSave} onOpenChange={(_, data) => this.setState({ showModalSave: data.open })}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle
                action={
                  <Button
                    appearance="subtle"
                    aria-label={intl.formatMessage({
                      id: "room-settings-sidebar.close-button",
                      defaultMessage: "Close"
                    })}
                    icon={<Dismiss24Regular />}
                    onClick={() => this.setState({ showModalSave: false })}
                  />
                }
              >
                <FormattedMessage id="avatar-editor.save-avatar-button" defaultMessage="Save avatar" />
              </DialogTitle>
              <DialogContent>
                <Input
                  appearance="filled-darker"
                  required
                  value={avatar?.name || ""}
                  onChange={e => this.setState({ avatar: { ...this.state.avatar, name: e.target.value } })}
                  placeholder={intl.formatMessage({
                    id: "avatar-editor.field.avatar-name",
                    defaultMessage: "Avatar name"
                  })}
                  className={styles.textField}
                />
                <p>
                  <FormattedMessage
                    id="avatar-editor.save-avatar-message"
                    defaultMessage="We're almost ready! To complete the creation you must enter a name and save your avatar."
                  />
                </p>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="outline">
                    <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                  </Button>
                </DialogTrigger>
                <Button appearance="primary" onClick={this.uploadAvatar}>
                  <FormattedMessage id="avatar-editor.submit-button.save" defaultMessage="Save" />
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {this.props.creationModal === "upload" && (
          <input
            style={{ display: "none" }}
            id="avatar-file_glb"
            type="file"
            accept="model/gltf+binary,.glb"
            onChange={e => {
              const file = e.target.files[0];
              e.target.value = null;
              this.inputFiles["glb"] = file;
              URL.revokeObjectURL(this.state.avatar.files["glb"]);
              this.setState({
                avatar: {
                  ...this.state.avatar,
                  files: {
                    ...this.state.avatar.files,
                    glb: URL.createObjectURL(file)
                  }
                },
                previewGltfUrl: this.getPreviewUrl("")
              });
              this.toggleSaveModal();
            }}
          />
        )}
      </div>
    );
  }
}

export default injectIntl(AvatarEditor);
